/**
 * The Attributes page as a form (5 Oct 2026 — after Celebrate's attribute setup,
 * in a modern style): every attribute shown the way a member's details look, with
 * a pencil (edit) beside its label on hover.
 */

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronDown, Pencil, UserRound } from 'lucide-react'
import { T } from '@/components/shell/orgTheme'
import type { AttrDef } from '@/lib/attributesModel'

/** A white section holding a 3-column grid of fields. */
/** `three`: never more than three columns, so rows line up from one card to the next. */
export function FormSection({ title, right, children, note, three }: { title: ReactNode; right?: ReactNode; children: ReactNode; note?: ReactNode; three?: boolean }) {
  return (
    <section style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, flex: 1, fontSize: 18, fontWeight: 600, color: T.dark }}>{title}</h2>
        {right}
      </div>
      {note}
      <div style={{ display: 'grid', gridTemplateColumns: three ? 'repeat(auto-fill, minmax(max(240px, calc((100% - 56px) / 3)), 1fr))' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '22px 28px' }}>{children}</div>
    </section>
  )
}

const PLACEHOLDER: Record<string, string> = {
  first_name: 'Jasmine', last_name: 'Mary', mobile: '98765 43210', language: 'English',
}

/** One field: label (+ required star), pencil on hover, and a soft preview of the input.
 *  Location shows as its three linked fields — Country · State · City — each opening its page. */
export function FieldPreview({ def, readOnly }: { def: AttrDef; readOnly?: boolean }) {
  // Basic information (6 Oct 2026): shown, not editable — no pencil, nothing to open.
  if (readOnly) return <FixedField label={def.label} required={def.required} hint="" value={<Preview def={def} />} />
  if (def.type === 'location') return <>{['Country', 'State', 'City'].map(l => <Cell key={l} def={def} label={l} preview={<>{hint(`Select a ${l.toLowerCase()}`)}<ChevronDown size={17} color={T.muted} /></>} required={def.required && l === 'Country'} />)}</>
  return <Cell def={def} label={def.label} preview={<Preview def={def} />} required={def.required} />
}

const hint = (t: string) => <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>

function Cell({ def, label, preview, required }: { def: AttrDef; label: string; preview: ReactNode; required?: boolean }) {
  const navigate = useNavigate()
  const to = `/org/attributes/${def.key}`
  const open = () => navigate(to)
  // The pencil appears on hover / keyboard focus (5 Oct 2026).
  return (
    <div className="group" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 30 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 500, color: T.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}{required && <span style={{ color: T.red, marginLeft: 2 }} aria-label="required">*</span>}
        </span>
        <button onClick={open} aria-label={`Edit ${label}`} title={`Edit ${def.label}`}
          className="hover:bg-[#F4F2F6] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', border: 'none', borderRadius: 8, background: 'transparent', cursor: 'pointer', color: T.purple }}>
          <Pencil size={15} />
        </button>
      </div>
      <button onClick={open} aria-label={`Edit ${label}`} className="hover:border-[#DCCFF3]"
        style={{ height: 46, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderRadius: 10, border: '1px solid transparent', background: '#F3F2F7', cursor: 'pointer', textAlign: 'left', color: T.faint, fontSize: 14.5, overflow: 'hidden' }}>
        {preview}
      </button>
    </div>
  )
}

/** What the input looks like for this type — a hint, never real data. */
function Preview({ def }: { def: AttrDef }) {
  const muted = (t: string) => <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
  const chevron = <ChevronDown size={17} color={T.muted} />
  switch (def.type) {
    case 'phone':
      return <><span style={{ color: T.ink, fontSize: 14 }}>🇮🇳 +91</span><span style={{ width: 1, height: 18, background: T.inputBorder }} />{muted(PLACEHOLDER.mobile)}</>
    case 'date':
      return <>{muted('Choose a date')}<Calendar size={17} color={T.muted} /></>
    case 'option':
      return <>{muted(`Select ${def.label.toLowerCase()}`)}{chevron}</>
    case 'multi':
      return <>{muted(`Select ${def.label.toLowerCase()} — one or more`)}{chevron}</>
    case 'person':
      return <><UserRound size={17} color={T.muted} />{muted(`Select a ${def.label.toLowerCase()}`)}{chevron}</>
    case 'location':
      return <>{muted('Country · State · City')}{chevron}</>
    case 'language':
      return <>{muted(PLACEHOLDER.language)}{chevron}</>
    case 'yesno':
      return <span style={{ display: 'flex', gap: 6 }}>{['Yes', 'No'].map(x => <span key={x} style={{ padding: '4px 14px', borderRadius: 7, background: '#fff', border: `1px solid ${T.inputBorder}`, fontSize: 13.5, color: T.ink }}>{x}</span>)}</span>
    case 'number':
      return muted('0')
    default:
      return muted(PLACEHOLDER[def.key] ?? (def.unique ? 'Unique for each member' : 'Text'))
  }
}

/** A fixed field that isn't an attribute (Email, Role) — same look, no eye or edit. */
export function FixedField({ label, required, hint, value }: { label: string; required?: boolean; hint: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 8 }}>
        <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: T.dark }}>{label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}</span>
        <span style={{ fontSize: 12, color: T.faint }}>{hint}</span>
      </div>
      <div style={{ height: 46, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderRadius: 10, background: '#F3F2F7', color: T.faint, fontSize: 14.5 }}>{value}</div>
    </div>
  )
}

